import React, { useEffect, useState } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Modal, Box, TextField, Button, Typography, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  CompanyDto,
  useEditCompanyControllerEditCompanyMutation,
  CampaignSipTrunkCreateRequestDto,
} from '@shared/services/apiService/apiService';
import { SipSettingsForm } from '@shared/components/forms/SipSettingsForm';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import { formatOptionLimitsConflictMessage } from '@widgets/Popup/CompanyLimitsPopup/utils';

interface EditCompanyPopupProps {
  company: CompanyDto;
  showDialogFlag: boolean;
  onCompanyEditSuccess: () => void;
  closeDialog: () => void;
}

export const EditCompanyPopup: React.FC<EditCompanyPopupProps> = ({
  company,
  onCompanyEditSuccess,
  showDialogFlag,
  closeDialog,
}) => {
  const { t } = useTranslation();
  const { showError, extractErrorMessage } = useErrorHandler();
  const [companyData, setCompanyData] = useState<any>(company);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [editCompany] = useEditCompanyControllerEditCompanyMutation();
  const [activeTab, setActiveTab] = useState(0);
  const [outboundSipValid, setOutboundSipValid] = useState(true);
  const [inboundSipValid, setInboundSipValid] = useState(true);

  const companyId = company.id;

  useEffect(() => {
    setCompanyData(company);
  }, [company]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyData({
      ...companyData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSipSettingsChange = (
    field: 'defaultOutboundSettings' | 'defaultInboundSettings',
    settings: CampaignSipTrunkCreateRequestDto | null,
  ) => {
    setCompanyData({
      ...companyData,
      [field]: settings,
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    try {
      await editCompany({ id: companyId, editCompanyRequestDto: companyData }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.saved'),
          description:
            t('superAdminMainPage.companies.success.updated', {
              name: companyData.name,
            }) || `Company "${companyData.name}" updated successfully`,
        },
      });

      onCompanyEditSuccess?.();
      closeDialog();
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      setErrors((fetchError.data as { [key: string]: string }) ?? {});

      const extracted = extractErrorMessage(error);
      const voiceNameById = new Map<string, string>();
      for (const voice of company.voices ?? []) {
        voiceNameById.set(voice.id, voice.name);
      }
      const providerTitleById = new Map<string, string>();
      for (const provider of company.llmProviders ?? []) {
        providerTitleById.set(provider.id, provider.title);
      }
      const message = formatOptionLimitsConflictMessage(
        extracted.message,
        voiceNameById,
        providerTitleById,
      );

      if (fetchError.status === 409 && message.toLowerCase().includes('voice')) {
        showError({
          title: t('superAdminMainPage.companies.companyLimits.voiceConflictTitle'),
          message,
          suggestion: t('superAdminMainPage.companies.companyLimits.voiceConflictSuggestion'),
        });
        return;
      }

      if (fetchError.status === 409 && message.toLowerCase().includes('provider')) {
        showError({
          title: t('superAdminMainPage.companies.companyLimits.llmProviderConflictTitle'),
          message,
          suggestion: t('superAdminMainPage.companies.companyLimits.llmProviderConflictSuggestion'),
        });
        return;
      }

      showError({
        title: 'Update Company Failed',
        message,
        suggestion: 'Please check your input and try again',
      });
    }
  };

  return (
    <Modal open={showDialogFlag} onClose={closeDialog}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxHeight: '80vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
        <Typography variant="h6" component="h2">
          {t('superAdminMainPage.companies.editCompany.title')}
        </Typography>
        {companyData && (
          <>
            <TextField
              margin="normal"
              fullWidth
              label="Name"
              name="name"
              value={companyData.name || ''}
              onChange={handleInputChange}
              error={Boolean(errors?.name)}
              helperText={errors?.name}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Industry Type"
              name="industryType"
              value={companyData.industryType || ''}
              onChange={handleInputChange}
              error={Boolean(errors?.industryType)}
              helperText={errors?.industryType}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Description"
              name="description"
              value={companyData.description || ''}
              onChange={handleInputChange}
              error={Boolean(errors?.description)}
              helperText={errors?.description}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Address"
              name="address"
              value={companyData.address || ''}
              onChange={handleInputChange}
              error={Boolean(errors?.address)}
              helperText={errors?.address}
            />

            <Box sx={{ mt: 3 }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="SIP settings tabs">
                <Tab label={t('superAdminMainPage.companies.tabs.sipOutbound')} />
                <Tab label={t('superAdminMainPage.companies.tabs.sipInbound')} />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ mt: 2 }}>
                  <SipSettingsForm
                    settings={companyData.defaultOutboundSettings}
                    onChange={(settings) =>
                      handleSipSettingsChange('defaultOutboundSettings', settings)
                    }
                    onValidationChange={setOutboundSipValid}
                    type="outbound"
                  />
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  <SipSettingsForm
                    settings={companyData.defaultInboundSettings}
                    onChange={(settings) =>
                      handleSipSettingsChange('defaultInboundSettings', settings)
                    }
                    onValidationChange={setInboundSipValid}
                    type="inbound"
                  />
                </Box>
              )}
            </Box>

            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              disabled={!outboundSipValid || !inboundSipValid}>
              Save
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
};
